import { Loader2, CopyCheck } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogMedia,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import type { Clubhouse_LeagueRoundV2Dto } from "../../types/leagues";

interface ApplyRoundSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Rounds the settings would be copied into (used to name them in the copy). */
  targetRounds: Clubhouse_LeagueRoundV2Dto[];
  onApply: () => void;
  isApplying?: boolean;
}

/** "Round 2 and Round 3", "Rounds 2, 3 and 4" — a friendly list of round names. */
function formatRoundList(rounds: Clubhouse_LeagueRoundV2Dto[]): string {
  const labels = rounds.map((r) => r.name || `Round ${r.roundNumber}`);
  if (labels.length === 1) return labels[0]!;
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
}

export function ApplyRoundSettingsDialog({
  open,
  onOpenChange,
  targetRounds,
  onApply,
  isApplying = false,
}: ApplyRoundSettingsDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent size="sm">
        <AlertDialogHeader>
          <AlertDialogMedia className="size-8 bg-primary/10">
            <CopyCheck className="size-4 text-primary" />
          </AlertDialogMedia>
          <AlertDialogTitle>Apply to your other rounds?</AlertDialogTitle>
          <AlertDialogDescription>
            Copy this round&apos;s conditions, scoring, and automation settings
            to {formatRoundList(targetRounds)}. Each round keeps its own dates,
            course, and holes.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="gap-3">
          <AlertDialogCancel
            onClick={() => onOpenChange(false)}
            disabled={isApplying}
            className="hover:text-primary hover:border-primary/30 rounded-lg"
          >
            Just this round
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={onApply}
            disabled={isApplying}
            className="active:scale-[0.97] rounded-lg"
          >
            {isApplying ? (
              <>
                <Loader2 className="animate-spin" />
                Applying…
              </>
            ) : (
              "Apply to all"
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
